import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  ApiToEntityMetricsAdapter,
  ApiToMetricsObjectAdapter,
  ApiToMetricsPageAdapter,
  ApiToMetricsWidgetAdapter,
  EntityId,
  EntityMetrics,
  IApiEntityMetrics,
  IApiMetricsPage,
  MetricsPage,
  NextStatisticsViewId,
  StatisticsPerspective,
} from '@fmnts/api/shared';
import { ApiConfigService, ApiRequestHelper } from '@fmnts/api/util';
import { maybeOptionHasValue } from '@fmnts/core';
import { map, Observable } from 'rxjs';
import { NextStatisticsFilterType } from '../api-model/api-enums';
import { IApiNextStatisticsFilterResult } from '../api-model/api-model';
import { deserializeFormReference, FormRef } from '../model/next-form';

declare interface NextStatisticsFilterQueryBase {
  /** ISO date string */
  date_from?: string;
  /** ISO date string */
  date_to?: string;
}

export declare interface NextStatisticsListFilterQuery
  extends NextStatisticsFilterQueryBase {
  team_id?: EntityId[];
  fundraiser_id?: EntityId[];
}

export declare type NextStatisticsDetailFilterQuery =
  NextStatisticsFilterQueryBase;

interface NextStatisticsRequestBase {
  /**
   * Perspective that is requested.
   *
   * Closely relates to domain entities.
   */
  perspective: StatisticsPerspective;
  /** Use to query specific form. */
  formRef?: FormRef;
  /** Filters. */
  filters?: NextStatisticsFilterQueryBase;
}

/**
 * Query to request a list of statistics.
 */
export interface NextStatisticsListQuery extends NextStatisticsRequestBase {
  /** Special views. */
  viewId?: NextStatisticsViewId;
  filters?: NextStatisticsListFilterQuery;
}

/**
 * Query to request the statistics for a specific entity.
 */
export interface NextStatisticsDetailQuery extends NextStatisticsRequestBase {
  /** ID of the requested entity. Depends on the perspective. */
  entityId: EntityId;
  filters?: NextStatisticsListFilterQuery;
}

/**
 * API service to request statistics for next.
 */
@Injectable({ providedIn: 'root' })
export class NextStatisticsApi {
  private static readonly rootUrl = '/api/next/v1/statistics';

  private readonly _metricWidgetAdapter = new ApiToMetricsWidgetAdapter();
  private readonly _metricsObjectAdapter = new ApiToMetricsObjectAdapter(
    this._metricWidgetAdapter,
  );
  private readonly _entityMetricsAdapter = new ApiToEntityMetricsAdapter(
    this._metricsObjectAdapter,
  );
  private readonly _pageAdapter = new ApiToMetricsPageAdapter(
    this._entityMetricsAdapter,
    this._metricsObjectAdapter,
  );

  constructor(
    private apiHelper: ApiRequestHelper,
    private configService: ApiConfigService,
    private http: HttpClient,
  ) {}

  /**
   * A list of metrics for entities.
   *
   * @param query Defines which data to query
   *
   * @returns
   */
  public list(
    query: NextStatisticsListQuery,
  ): Observable<MetricsPage<EntityMetrics>> {
    const { perspective, formRef, filters, viewId } = query;

    // Determine which view to query.
    const view = viewId ? [viewId] : [];
    const formSubview = _getFormSubview(formRef);

    // Prepare request
    const url = this.configService.buildApiUrl([
      NextStatisticsApi.rootUrl,
      perspective,
      ...view,
      ...formSubview,
    ]);
    const params = this.apiHelper.makeParams(filters);
    const headers = this.apiHelper.makeDefaultHeaders({});

    return this.http
      .get<IApiMetricsPage>(url, { headers, params })
      .pipe(map((response) => this._pageAdapter.adapt(response)));
  }

  /**
   * Metrics for a single entity.
   *
   * @param query Defines which data to query
   * @returns
   */
  public get(query: NextStatisticsDetailQuery): Observable<EntityMetrics> {
    const { perspective, entityId, formRef, filters } = query;

    // Determine which view to query.
    const formSubview = _getFormSubview(formRef);

    // Prepare request
    const url = this.configService.buildApiUrl([
      NextStatisticsApi.rootUrl,
      perspective,
      entityId,
      ...formSubview,
    ]);
    const params = this.apiHelper.makeParams(filters);
    const headers = this.apiHelper.makeDefaultHeaders({});

    return this.http
      .get<IApiEntityMetrics>(url, { headers, params })
      .pipe(map((response) => this._entityMetricsAdapter.adapt(response)));
  }

  /**
   * Fetches filters, which can be used for Next-Statistics
   *
   * @param types list of filters, that can be used for the view
   * @returns Array of FilterResults
   */
  public getFilters(
    types: `${NextStatisticsFilterType}`[],
  ): Observable<IApiNextStatisticsFilterResult[]> {
    const url = this.configService.buildApiUrl([
      NextStatisticsApi.rootUrl,
      'filter-options',
    ]);
    const params = _makeFilterParams(types);

    return this.http.get<IApiNextStatisticsFilterResult[]>(url, { params });
  }
}

function _getFormSubview(
  formRef: FormRef | null | undefined,
): (string | number)[] {
  if (!maybeOptionHasValue(formRef)) {
    return [];
  }

  const { formType, id } = deserializeFormReference(formRef);
  return [formType, id];
}

/**
 * Custom function to create Filter Params for Next-Statistics
 * Assigns an Array of filter-types to HTTPParams using the key
 * `filter_types` for each filter.
 *
 * @param types Array of filters to apply
 * @returns HTTPParams
 */
function _makeFilterParams(types: `${NextStatisticsFilterType}`[]) {
  return types.reduce(
    (p, key) => p.append('filter_types', key),
    new HttpParams(),
  );
}
